import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Card,
  Chip,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AccessTime,
  People,
  ShowChart,
  ArrowForward,
  ArrowBack,
  FiberManualRecord,
  BarChart,
} from "@mui/icons-material";
import SarIcon from "../icons/SarIcon";
import {
  authApi,
  useGetSecondaryAssetsQuery,
  useGetSecondaryAssetDetailQuery,
  useGetSecondaryMarketOverviewQuery,
  useSecondaryBuyMutation,
  useSecondarySellMutation,
} from "../../redux/api/authApi";
import { getInvestorApiBase } from "../../utils/investorApiBase";

// ---------- Image URL helper ----------
function resolveImageUrl(imageUrl) {
  if (!imageUrl) return "";
  const s = String(imageUrl).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const path = s.replace(/^\//, "");
  const base = getInvestorApiBase();
  if (!base) return path;
  return path.startsWith("uploads/") ? `${base}/${path}` : `${base}/uploads/${path}`;
}

function formatTradeTime(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

function formatCompact(n) {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  const x = Number(n);
  if (Math.abs(x) >= 1_000_000_000) return (x / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "") + "B";
  if (Math.abs(x) >= 1_000_000) return (x / 1_000_000).toFixed(2).replace(/\.?0+$/, "") + "M";
  if (Math.abs(x) >= 1_000) return (x / 1_000).toFixed(2).replace(/\.?0+$/, "") + "K";
  return Number(x.toFixed(2)).toString();
}

function AssetSelectorCard({ asset, selected, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        minWidth: 165,
        cursor: "pointer",
        border: selected ? "2px solid #06b6d4" : (theme) => `1px solid ${theme.palette.border.light}`,
        borderRadius: 2,
        p: 1.5,
        boxShadow: selected ? "0 0 0 3px rgba(6,182,212,0.12)" : "none",
        bgcolor: "background.paper",
        transition: "border-color 0.15s",
        "&:hover": { borderColor: "#06b6d4" },
        flexShrink: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.border.light,
            backgroundImage: asset.imageUrl ? `url(${asset.imageUrl})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {!asset.imageUrl && "🏢"}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            fontWeight={700}
            fontSize={13}
            sx={{
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 100,
            }}
          >
            {asset.name}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" justifyContent="space-between" mb={0.4}>
        <Typography fontSize={11} sx={{ color: "text.muted" }}>Price</Typography>
        <Typography fontSize={11} sx={{ color: "text.muted" }}>24h</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography fontWeight={700} fontSize={13} sx={{ color: "text.primary" }}>
          SAR {formatCompact(asset.price)}
        </Typography>
        <Typography
          fontWeight={600}
          fontSize={12}
          sx={{ color: asset.priceChange24h >= 0 ? "#16a34a" : "#dc2626" }}
        >
          {asset.priceChange24h >= 0 ? "+" : ""}
          {asset.priceChange24h}%
        </Typography>
      </Stack>
      {asset.ownedTokens > 0 && (
        <Typography fontSize={11} sx={{ color: "text.muted" }}>
          {asset.ownedTokens} tokens owned
        </Typography>
      )}
    </Card>
  );
}

const InvestorSecondaryMarket = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [orderSide, setOrderSide] = useState("buy");
  const [orderType, setOrderType] = useState("limit");
  const [pricePerToken, setPricePerToken] = useState("");
  const [amount, setAmount] = useState("");
  const [activeTab, setActiveTab] = useState("orderbook");

  const { data, isLoading, isError, error, refetch } = useGetSecondaryAssetsQuery(undefined, { refetchOnMountOrArgChange: true });
  // Initial load without query params; background refresh every 5s uses ?cached=true (see interval below).
  const { data: overviewRes, refetch: refetchOverview } = useGetSecondaryMarketOverviewQuery(undefined, { refetchOnMountOrArgChange: true });
  const ov = overviewRes?.data ?? {};

  useEffect(() => {
    const id = setInterval(() => {
      dispatch(
        authApi.endpoints.getSecondaryMarketOverview.initiate(
          { cached: true },
          { forceRefetch: true }
        )
      );
    }, 5000);
    return () => clearInterval(id);
  }, [dispatch]);

  const volChange = ov.volume24hChange ?? 0;
  const traderChange = ov.activeTradersChange ?? 0;

  const STATS = [
    {
      label: "24h Volume",
      value: ov.volume24h != null ? `SAR ${formatCompact(ov.volume24h)}` : "—",
      // sub: volChange !== 0 ? `${volChange > 0 ? "+" : volChange < 0 ? "-" : ""}${volChange}%` : "—",
      sub: "volume",
      subColor: volChange > 0 ? "#16a34a" : volChange < 0 ? "#dc2626" : "#6b7280",
      icon: <ShowChart sx={{ fontSize: 22 }} />,
      iconColor: "#16a34a",
    },
    {
      label: "Active Traders",
      value: ov.activeTraders24h != null ? String(ov.activeTraders24h) : "—",
      // sub: traderChange !== 0 ? `${traderChange > 0 ? "+" : traderChange < 0 ? "-" : ""}${traderChange}%` : "—",
      sub: "trader",
      subColor: traderChange > 0 ? "#16a34a" : traderChange < 0 ? "#dc2626" : "#6b7280",
      icon: <People sx={{ fontSize: 22 }} />,
      iconColor: "#0d9488",
    },
    {
      label: "Settlement Time",
      value: ov.settlementTime ?? "—",
      sub: "Instant",
      subColor: "#6b7280",
      icon: <AccessTime sx={{ fontSize: 22 }} />,
      iconColor: "#9333ea",
    },
    {
      label: "Trading Fee",
      value: ov.tradingFeePercent != null ? `${ov.tradingFeePercent}%` : "—",
      sub: "Per trade",
      subColor: "#6b7280",
      icon: <SarIcon sx={{ fontSize: 22 }} />,
      iconColor: "#f59e0b",
    },
  ];
  const assets = Array.isArray(data?.data) ? data.data.map((a) => ({
    ...a,
    imageUrl: resolveImageUrl(a.imageUrl),
  })) : [];

  // Auto-select first asset once data arrives
  useEffect(() => {
    if (assets.length === 0) return;
    if (selectedAssetId && assets.find((a) => a.assetId === selectedAssetId)) return;
    const first = assets[0];
    setSelectedAssetId(first.assetId);
    setPricePerToken(String(first.price));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.length]);

  const selectedAsset = assets.find((a) => a.assetId === selectedAssetId) || assets[0] || null;

  // Fetch detail for the selected asset: initial + manual refetch use no query params;
  // background refresh every 5s uses ?cached=true (see interval below).
  const {
    data: detailRes,
    isLoading: detailLoading,
    refetch: refetchDetail,
  } = useGetSecondaryAssetDetailQuery(
    { assetId: selectedAssetId },
    { skip: !selectedAssetId, refetchOnMountOrArgChange: true }
  );
  const detail = detailRes?.data ?? null;

  useEffect(() => {
    if (!selectedAssetId) return undefined;
    const id = setInterval(() => {
      dispatch(
        authApi.endpoints.getSecondaryAssetDetail.initiate(
          { assetId: selectedAssetId, cached: true },
          { forceRefetch: true }
        )
      );
    }, 5000);
    return () => clearInterval(id);
  }, [selectedAssetId, dispatch]);

  const [placeBuy, { isLoading: buyLoading }] = useSecondaryBuyMutation();
  const [placeSell, { isLoading: sellLoading }] = useSecondarySellMutation();
  const orderLoading = buyLoading || sellLoading;
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState(null);

  const handlePlaceOrder = async () => {
    if (!selectedAssetId || !amount) return;
    setOrderError(null);
    const payload = {
      assetId: selectedAssetId,
      tokenAmount: Number(amount),
      orderType,
      ...(orderType === "limit" && { pricePerToken: Number(pricePerToken) }),
    };
    try {
      if (orderSide === "sell") {
        await placeSell(payload).unwrap();
      } else {
        await placeBuy(payload).unwrap();
      }
      setAmount("");
      setOrderSuccess(true);
      setTimeout(() => setOrderSuccess(false), 3000);
      refetch();
      refetchDetail();
      refetchOverview();
    } catch (err) {
      setOrderError(err?.data?.message || "Order failed. Please try again.");
    }
  };

  // Keep pricePerToken in sync with live price from detail API; amount stays blank
  useEffect(() => {
    if (!detail) return;
    setPricePerToken(String(detail.price));
  }, [detail?.price, selectedAssetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxSellAmount = detail?.owned ?? selectedAsset?.ownedTokens ?? 0;
  const amountExceedsOwned = orderSide === "sell" && amount !== "" && Number(amount) > maxSellAmount;

  const price = Number(pricePerToken) || 0;
  const qty = Number(amount) || 0;
  const subtotal = Math.round(price * qty * 100) / 100;
  const fee = Math.round(subtotal * 0.0025 * 100) / 100;
  const total = Math.round((orderSide === "sell" ? subtotal - fee : subtotal + fee) * 100) / 100;

  // Market depth from detail API
  const md = detail?.orderbook?.marketDepth;
  const buyDepth = md?.buy ?? 0;
  const sellDepth = md?.sell ?? 0;
  const buyRatio = md?.buyPercent ?? (buyDepth + sellDepth > 0 ? Math.round((buyDepth / (buyDepth + sellDepth)) * 100) : 0);
  const sellRatio = md?.sellPercent ?? (100 - buyRatio);

  // Order book from detail API
  const obData = detail?.orderbook ?? {};
  const orderBook = {
    buys: Array.isArray(obData.bids) ? obData.bids : [],
    sells: Array.isArray(obData.asks) ? obData.asks : [],
  };
  const bestBid = obData.bestBid ?? null;
  const bestAsk = obData.bestAsk ?? null;
  const spreadVal = obData.spread != null ? `${obData.spread}%` : "—";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.accent.teal.chipText }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/investor/viewinvest", { state: { viewTab: "my" } })}
          sx={{ mb: 2, textTransform: "none", color: "#475569", fontWeight: 600 }}
        >
          Back to My Investments
        </Button>
        <Alert severity="error" onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Failed to load tradable assets."}
        </Alert>
      </Box>
    );
  }

  if (assets.length === 0) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/investor/viewinvest", { state: { viewTab: "my" } })}
          sx={{ mb: 2, textTransform: "none", color: "#475569", fontWeight: 600 }}
        >
          Back to My Investments
        </Button>
        <Alert severity="info">No tradable assets are available on the secondary market right now.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Back Link */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/investor/viewinvest", { state: { viewTab: "my" } })}
        sx={{ mb: 2, textTransform: "none", color: "#475569", fontWeight: 600 }}
      >
        Back to My Investments
      </Button>

      {/* Page Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: "text.primary", letterSpacing: -0.5 }}
          >
            Secondary Market
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
            Trade tokenized assets with instant settlement
          </Typography>
        </Box>
        <Chip
          icon={
            <FiberManualRecord
              sx={{ fontSize: "10px !important", color: "#16a34a !important" }}
            />
          }
          label="Market Open"
          sx={{
            bgcolor: "#f0fdf4",
            color: "#16a34a",
            fontWeight: 700,
            fontSize: 13,
            border: "1px solid #bbf7d0",
            borderRadius: 999,
            height: 32,
          }}
        />
      </Stack>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STATS.map((stat) => (
          <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
                bgcolor: "background.paper",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.muted", fontWeight: 600 }}
                    display="block"
                    mb={0.5}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={800}
                    sx={{ color: "text.primary", lineHeight: 1.2 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: stat.subColor, fontWeight: 600 }}
                  >
                    {stat.sub}
                  </Typography>
                </Box>
                <Box sx={{ color: stat.iconColor }}>{stat.icon}</Box>
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Asset Selector */}
      <Card
        sx={{
          p: 2,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
          mb: 3,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontWeight={700} sx={{ color: "text.primary" }}>
            Select Asset
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderColor: (theme) => theme.palette.border.light,
              color: "text.muted",
              borderRadius: 1.5,
              fontSize: 13,
            }}
          >
            My Assets
          </Button>
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "thin" }}
        >
          {assets.map((asset) => (
            <AssetSelectorCard
              key={asset.assetId}
              asset={asset}
              selected={selectedAssetId === asset.assetId}
              onClick={() => {
                setSelectedAssetId(asset.assetId);
                setPricePerToken(String(asset.price));
                setAmount(String(asset.ownedTokens > 0 ? asset.ownedTokens : ""));
              }}
            />
          ))}
        </Stack>
      </Card>

      {/* Main 3-column Layout */}
      <Grid container spacing={2} alignItems="flex-start">
        {/* ── Place Order ── */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              boxShadow: (theme) => theme.palette.shadow.statCard,
              bgcolor: "background.paper",
            }}
          >
            {/* Title */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                Place Order
              </Typography>
              <Chip
                size="small"
                icon={<AccessTime sx={{ fontSize: "14px !important" }} />}
                label="Instant"
                sx={{
                  bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
                  color: (theme) => theme.palette.accent.cyan.text,
                  fontWeight: 600,
                  fontSize: 12,
                  border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
                }}
              />
            </Stack>

            {/* Buy / Sell Toggle */}
            <Stack direction="row" spacing={1} mb={2.5}>
              <Button
                fullWidth
                startIcon={<TrendingUp />}
                onClick={() => setOrderSide("buy")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  py: 1,
                  border: "1px solid",
                  ...(orderSide === "buy"
                    ? {
                        bgcolor: "#16a34a",
                        color: "#fff",
                        borderColor: "#16a34a",
                        "&:hover": { bgcolor: "#15803d", borderColor: "#15803d" },
                      }
                    : {
                        bgcolor: "background.paper",
                        color: "text.muted",
                        borderColor: (theme) => theme.palette.border.light,
                      }),
                }}
              >
                Buy
              </Button>
              <Button
                fullWidth
                startIcon={<TrendingDown />}
                onClick={() => setOrderSide("sell")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  py: 1,
                  border: "1px solid",
                  ...(orderSide === "sell"
                    ? {
                        bgcolor: "#dc2626",
                        color: "#fff",
                        borderColor: "#dc2626",
                        "&:hover": { bgcolor: "#b91c1c", borderColor: "#b91c1c" },
                      }
                    : {
                        bgcolor: "background.paper",
                        color: "text.muted",
                        borderColor: (theme) => theme.palette.border.light,
                      }),
                }}
              >
                Sell
              </Button>
            </Stack>

            {/* Order Type */}
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ color: "text.muted" }}
              display="block"
              mb={1}
            >
              Order Type
            </Typography>
            <Stack direction="row" spacing={1} mb={2.5}>
              {[
                { key: "limit", label: "Limit Order", sub: "Set price" },
                { key: "market", label: "Market Order", sub: "Best price" },
              ].map((t) => (
                <Box
                  key={t.key}
                  onClick={() => setOrderType(t.key)}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: "1px solid",
                    cursor: "pointer",
                    borderColor:
                      orderType === t.key
                        ? "#06b6d4"
                        : (theme) => theme.palette.border.light,
                    bgcolor:
                      orderType === t.key
                        ? (theme) => theme.palette.accent.cyan.bgLight
                        : "background.paper",
                  }}
                >
                  <Typography
                    fontWeight={700}
                    fontSize={13}
                    sx={{
                      color:
                        orderType === t.key
                          ? (theme) => theme.palette.accent.cyan.text
                          : "text.primary",
                    }}
                  >
                    {t.label}
                  </Typography>
                  <Typography fontSize={11} sx={{ color: "text.muted" }}>
                    {t.sub}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Price per Token — Limit only */}
            {orderType === "limit" ? (
              <>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ color: "text.muted" }}
                  display="block"
                  mb={0.75}
                >
                  Price per Token
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={pricePerToken}
                  onChange={(e) => setPricePerToken(e.target.value)}
                  sx={{
                    mb: 0.75,
                    "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography fontSize={13} sx={{ color: "text.muted" }}>SAR</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "text.muted" }}
                  display="block"
                  mb={2}
                >
                  Market: SAR {formatCompact(detail?.price ?? selectedAsset?.price)} • Spread: {spreadVal}
                </Typography>
              </>
            ) : (
              /* Market Order — show live price info box */
              <Box
                sx={{
                  bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
                  border: (theme) => `1px solid ${theme.palette.accent.teal.border}`,
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.25,
                  mb: 2,
                }}
              >
                <Typography
                  fontSize={13}
                  fontWeight={600}
                  sx={{ color: (theme) => theme.palette.accent.cyan.text }}
                >
                  Market price: SAR {formatCompact(detail?.price ?? selectedAsset?.price)}
                </Typography>
              </Box>
            )}

            {/* Amount */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.75}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "text.muted" }}
              >
                Amount (Tokens)
              </Typography>
              {/* Sell only: show Max (tokens owned) */}
              {orderSide === "sell" && (
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    color: (theme) => theme.palette.accent.cyan.text,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => setAmount(String(detail?.owned ?? selectedAsset?.ownedTokens ?? 0))}
                >
                  Max: {detail?.owned ?? selectedAsset?.ownedTokens ?? 0}
                </Typography>
              )}
            </Stack>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter amount"
              value={amount}
              error={amountExceedsOwned}
              helperText={amountExceedsOwned ? `Cannot exceed owned tokens (${maxSellAmount})` : undefined}
              inputProps={{ inputMode: "numeric" }}
              onChange={(e) => { const v = e.target.value; if (v === "" || /^\d+$/.test(v)) setAmount(v); }}
              sx={{ mb: orderSide === "buy" ? 1 : 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            />

            {/* Quick-amount presets — Buy only (both Limit and Market) */}
            {orderSide === "buy" && (
              <Stack direction="row" spacing={1} mb={2}>
                {[10, 25, 50, 100].map((preset) => (
                  <Button
                    key={preset}
                    onClick={() => setAmount(String(preset))}
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: 13,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: (theme) => `1px solid ${theme.palette.border.light}`,
                      bgcolor: Number(amount) === preset ? (theme) => theme.palette.accent.cyan.bgLight : "background.paper",
                      color: Number(amount) === preset ? (theme) => theme.palette.accent.cyan.text : "text.muted",
                      "&:hover": { bgcolor: (theme) => theme.palette.accent.cyan.bgLight },
                    }}
                  >
                    {preset}
                  </Button>
                ))}
              </Stack>
            )}

            {/* Order Summary */}
            <Box
              sx={{
                bgcolor: "background.subtle",
                borderRadius: 1.5,
                p: 2,
                mb: 2,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" sx={{ color: "text.muted" }}>
                  Subtotal
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary" }}>
                  SAR {formatCompact(subtotal)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" sx={{ color: "text.muted" }}>
                  Fee (0.25%)
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary" }}>
                  SAR {formatCompact(fee)}
                </Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary" }}>
                  Total
                </Typography>
                <Typography variant="body2" fontWeight={800} sx={{ color: "text.primary" }}>
                  SAR {formatCompact(total)}
                </Typography>
              </Stack>
            </Box>

            {/* Order feedback */}
            {orderSuccess && (
              <Alert severity="success" sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5 }}>
                Order placed successfully!
              </Alert>
            )}
            {orderError && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5, py: 0.5 }} onClose={() => setOrderError(null)}>
                {orderError}
              </Alert>
            )}

            {/* Action Button */}
            <Button
              fullWidth
              variant="contained"
              endIcon={orderLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ArrowForward />}
              disabled={orderLoading || !selectedAssetId || !amount || amountExceedsOwned}
              onClick={handlePlaceOrder}
              sx={{
                py: 1.5,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                boxShadow: "none",
                bgcolor: orderSide === "sell" ? "#dc2626" : "#16a34a",
                "&:hover": {
                  bgcolor: orderSide === "sell" ? "#b91c1c" : "#15803d",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  bgcolor: orderSide === "sell" ? "#dc262699" : "#16a34a99",
                  color: "#fff",
                },
              }}
            >
              {orderLoading ? "Placing Order…" : `Place ${orderSide === "sell" ? "Sell" : "Buy"} Order`}
            </Button>
          </Card>
        </Grid>

        {/* ── Market Data ── */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.border.light}`,
              boxShadow: (theme) => theme.palette.shadow.statCard,
              bgcolor: "background.paper",
            }}
          >
            <Typography fontWeight={700} sx={{ color: "text.primary" }} mb={2}>
              Market Data
            </Typography>

            {/* Asset Info */}
            <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: (theme) => theme.palette.border.light,
                  backgroundImage: selectedAsset.imageUrl ? `url(${selectedAsset.imageUrl})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  flexShrink: 0,
                }}
              >
                {!selectedAsset.imageUrl && "🏢"}
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                  {selectedAsset.name}
                </Typography>
              </Box>
              {(detail?.owned ?? selectedAsset?.ownedTokens ?? 0) > 0 && (
                <Chip
                  label={`${detail?.owned ?? selectedAsset?.ownedTokens} Owned`}
                  size="small"
                  sx={{
                    bgcolor: "#dbeafe",
                    color: "#1d4ed8",
                    fontWeight: 700,
                    borderRadius: 999,
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>

            {/* Price / Volume / Market Cap */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid size={4}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.muted" }}
                  display="block"
                  mb={0.25}
                >
                  Price
                </Typography>
                <Typography fontWeight={700} fontSize={15} sx={{ color: "text.primary" }}>
                  SAR {formatCompact(selectedAsset.price)}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    color: selectedAsset.priceChange24h >= 0 ? "#16a34a" : "#dc2626",
                  }}
                >
                  {selectedAsset.priceChange24h >= 0 ? "↑" : "↓"}
                  {Math.abs(selectedAsset.priceChange24h)}%
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.muted" }}
                  display="block"
                  mb={0.25}
                >
                  Volume
                </Typography>
                <Typography fontWeight={700} fontSize={15} sx={{ color: "text.primary" }}>
                  {detail ? `SAR ${formatCompact(detail.volume24h)}` : "—"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.muted" }}>
                  24h
                </Typography>
              </Grid>
              <Grid size={4}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.muted" }}
                  display="block"
                  mb={0.25}
                >
                  Market Cap
                </Typography>
                <Typography fontWeight={700} fontSize={15} sx={{ color: "text.primary" }}>
                  {detail ? `SAR ${formatCompact(detail.marketCap)}` : "—"}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.muted" }}>
                  Total
                </Typography>
              </Grid>
            </Grid>

            {/* Tabs — full-width pill segmented control */}
            <Box
              sx={{
                display: "flex",
                bgcolor: (theme) => theme.palette.border.light,
                borderRadius: 999,
                p: 0.5,
                mb: 2,
                width: "100%",
              }}
            >
              {[
                { key: "orderbook", label: "Order Book" },
                { key: "recenttrades", label: "Recent Trades" },
              ].map((tab) => (
                <Button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    px: 1,
                    py: 0.75,
                    borderRadius: 999,
                    border: "none",
                    bgcolor: activeTab === tab.key ? "background.paper" : "transparent",
                    color: activeTab === tab.key ? "text.primary" : "text.muted",
                    boxShadow: activeTab === tab.key ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
                    "&:hover": {
                      bgcolor: activeTab === tab.key ? "background.paper" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {/* Recent Trades */}
            {activeTab === "recenttrades" && (
              Array.isArray(detail?.recentTrades) && detail.recentTrades.length > 0 ? (
                <Box>
                  {/* Header row */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                      pb: 1,
                      mb: 0.5,
                      borderBottom: (theme) => `1px solid ${theme.palette.border.light}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={600} sx={{ color: "text.muted", flex: 1 }}>
                      Time
                    </Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "text.muted", flex: 1.2, textAlign: "center" }}>
                      Price
                    </Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "text.muted", flex: 1, textAlign: "center" }}>
                      Amount
                    </Typography>
                    <Typography variant="caption" fontWeight={600} sx={{ color: "text.muted", flex: 0.8, textAlign: "right" }}>
                      Type
                    </Typography>
                  </Stack>
                  {detail.recentTrades.map((trade, i) => {
                    const isBuy = String(trade.side ?? trade.type ?? "").toLowerCase() !== "sell";
                    return (
                      <Stack
                        key={i}
                        direction="row"
                        alignItems="center"
                        py={0.75}
                        sx={{
                          borderBottom: (theme) => `1px solid ${theme.palette.border.light}`,
                          "&:last-of-type": { borderBottom: "none" },
                        }}
                      >
                        <Typography fontSize={12} sx={{ color: "text.muted", flex: 1 }}>
                          {formatTradeTime(trade.time ?? trade.createdAt)}
                        </Typography>
                        <Typography
                          fontSize={12}
                          fontWeight={600}
                          sx={{ color: isBuy ? "#16a34a" : "#dc2626", flex: 1.2, textAlign: "center" }}
                        >
                          SAR {formatCompact(trade.price)}
                        </Typography>
                        <Typography fontSize={12} sx={{ color: "text.primary", flex: 1, textAlign: "center" }}>
                          {trade.quantity ?? trade.amount ?? "—"}
                        </Typography>
                        <Box sx={{ flex: 0.8, display: "flex", justifyContent: "flex-end" }}>
                          <Chip
                            label={isBuy ? "Buy" : "Sell"}
                            size="small"
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: 11,
                              borderRadius: 999,
                              border: "1px solid",
                              bgcolor: "transparent",
                              borderColor: isBuy ? "#16a34a" : "#dc2626",
                              color: isBuy ? "#16a34a" : "#dc2626",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                      </Stack>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: "text.muted", textAlign: "center", display: "block", py: 4 }}>No recent trades</Typography>
              )
            )}

            {activeTab === "orderbook" && (
            <>
            {/* Best Bid / Spread / Best Ask */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              mb={1.5}
              sx={{
                px: 1,
                py: 1,
                bgcolor: "background.subtle",
                borderRadius: 1.5,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "text.muted" }} display="block">
                  Best Bid
                </Typography>
                <Typography fontWeight={700} sx={{ color: "#16a34a" }}>
                  {bestBid != null ? `SAR ${formatCompact(bestBid)}` : "—"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "text.muted" }} display="block">
                  Spread
                </Typography>
                <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                  {spreadVal}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ color: "text.muted" }} display="block">
                  Best Ask
                </Typography>
                <Typography fontWeight={700} sx={{ color: "#dc2626" }}>
                  {bestAsk != null ? `SAR ${formatCompact(bestAsk)}` : "—"}
                </Typography>
              </Box>
            </Stack>

            {/* Order Book Table */}
            <Grid container spacing={2}>
              {/* Buy Side */}
              <Grid size={6}>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                  <TrendingUp sx={{ fontSize: 14, color: "#16a34a" }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#16a34a" }}
                  >
                    Buy
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" sx={{ color: "text.muted" }}>
                    Price
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.muted" }}>
                    Amt
                  </Typography>
                </Stack>
                {orderBook.buys.length === 0 ? (
                  <Typography variant="caption" sx={{ color: "text.muted", textAlign: "center", display: "block", py: 2 }}>No bids</Typography>
                ) : orderBook.buys.map((row, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" py={0.3}>
                    <Typography fontSize={12} sx={{ color: "#16a34a" }}>
                      SAR {formatCompact(row.price)}
                    </Typography>
                    <Typography fontSize={12} sx={{ color: "text.primary" }}>
                      {row.quantity ?? row.amount ?? row.qty ?? "—"}
                    </Typography>
                  </Stack>
                ))}
              </Grid>
              {/* Sell Side */}
              <Grid size={6}>
                <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                  <TrendingDown sx={{ fontSize: 14, color: "#dc2626" }} />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{ color: "#dc2626" }}
                  >
                    Sell
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" sx={{ color: "text.muted" }}>
                    Price
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.muted" }}>
                    Amt
                  </Typography>
                </Stack>
                {orderBook.sells.length === 0 ? (
                  <Typography variant="caption" sx={{ color: "text.muted", textAlign: "center", display: "block", py: 2 }}>No asks</Typography>
                ) : orderBook.sells.map((row, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" py={0.3}>
                    <Typography fontSize={12} sx={{ color: "#dc2626" }}>
                      SAR {formatCompact(row.price)}
                    </Typography>
                    <Typography fontSize={12} sx={{ color: "text.primary" }}>
                      {row.quantity ?? row.amount ?? row.qty ?? "—"}
                    </Typography>
                  </Stack>
                ))}
              </Grid>
            </Grid>
            </>
            )}
          </Card>
        </Grid>

        {/* ── Right Panel ── */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Stack spacing={2}>
            {/* Your Position */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid #a5f3fc",
                boxShadow: (theme) => theme.palette.shadow.statCard,
                bgcolor: (theme) => theme.palette.accent.cyan.bgLight,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ShowChart
                  sx={{ color: (theme) => theme.palette.accent.cyan.text, fontSize: 20 }}
                />
                <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                  Your Position
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: "text.muted" }}
                display="block"
                mb={0.25}
              >
                Holdings
              </Typography>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{ color: "text.primary", lineHeight: 1 }}
              >
                {detailLoading ? "…" : (detail?.owned ?? selectedAsset?.ownedTokens ?? 0)}
              </Typography>
              <Typography fontSize={13} sx={{ color: "text.muted", mb: 1.5 }}>
                tokens
              </Typography>

              <Typography
                variant="caption"
                sx={{ color: "text.muted" }}
                display="block"
                mb={0.25}
              >
                Value
              </Typography>
              <Typography fontWeight={800} fontSize={20} sx={{ color: "text.primary", mb: 2 }}>
                {detailLoading ? "…" : `SAR ${formatCompact(detail?.value ?? 0)}`}
              </Typography>

              <Button
                fullWidth
                variant="outlined"
                disabled={(detail?.owned ?? selectedAsset?.ownedTokens ?? 0) === 0}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  borderColor: "#06b6d4",
                  color: (theme) => theme.palette.accent.cyan.text,
                  "&:hover": {
                    bgcolor: (theme) => theme.palette.accent.cyan.bg,
                    borderColor: "#0891b2",
                  },
                }}
                onClick={() => {
                  setOrderSide("sell");
                  setAmount(String(detail?.owned ?? selectedAsset?.ownedTokens ?? 0));
                }}
              >
                Sell All
              </Button>
            </Card>

            {/* Market Depth */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
                bgcolor: "background.paper",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <BarChart
                  sx={{ color: (theme) => theme.palette.accent.cyan.text, fontSize: 20 }}
                />
                <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                  Market Depth
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: "text.muted" }}
                display="block"
                mb={0.5}
              >
                Buy
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
                <LinearProgress
                  variant="determinate"
                  value={buyRatio}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "#a3f8c1",
                    "& .MuiLinearProgress-bar": { bgcolor: "#16a34a", borderRadius: 999 },
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "text.primary", minWidth: 38, textAlign: "right" }}
                >
                  {buyDepth.toLocaleString()}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: "text.muted" }}
                display="block"
                mb={0.5}
              >
                Sell
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <LinearProgress
                  variant="determinate"
                  value={sellRatio}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: "#fca5a5",
                    "& .MuiLinearProgress-bar": { bgcolor: "#dc2626", borderRadius: 999 },
                  }}
                />
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: "text.primary", minWidth: 38, textAlign: "right" }}
                >
                  {sellDepth.toLocaleString()}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: "text.muted" }}
                display="block"
                mb={0.5}
              >
                Ratio
              </Typography>
              {buyRatio === 0 && sellRatio === 0 ? (
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    mb: 0.5,
                    display: "flex",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ width: "50%", bgcolor: "#a3f8c1" }} />
                  <Box sx={{ width: "50%", bgcolor: "#fca5a5" }} />
                </Box>
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={buyRatio}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    mb: 0.5,
                    bgcolor: "#dc2626",
                    "& .MuiLinearProgress-bar": { bgcolor: "#16a34a", borderRadius: 999 },
                  }}
                />
              )}
              <Typography variant="caption" sx={{ color: "text.muted" }}>
                {buyRatio}% / {sellRatio}%
              </Typography>
            </Card>

            {/* Statistics */}
            <Card
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.border.light}`,
                boxShadow: (theme) => theme.palette.shadow.statCard,
                bgcolor: "background.paper",
              }}
            >
              <Typography fontWeight={700} sx={{ color: "text.primary" }} mb={1.5}>
                Statistics
              </Typography>
              {[
                { label: "Market Cap", value: detail ? `SAR ${formatCompact(detail.marketCap)}` : "—" },
                { label: "24h High", value: detail ? `SAR ${formatCompact(detail.high24h)}` : "—", color: "#16a34a" },
                { label: "24h Low", value: detail ? `SAR ${formatCompact(detail.low24h)}` : "—", color: "#dc2626" },
                { label: "Traders", value: detail ? String(detail.traders24h) : "—" },
              ].map((stat) => (
                <Stack
                  key={stat.label}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  py={0.75}
                  sx={{
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.border.light}`,
                    "&:last-of-type": { borderBottom: "none" },
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.muted" }}>
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: stat.color || "text.primary" }}
                  >
                    {stat.value}
                  </Typography>
                </Stack>
              ))}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                pt={0.75}
              >
                <Typography variant="body2" sx={{ color: "text.muted" }}>Liquidity</Typography>
                <Chip
                  label={detail?.liquidity ?? "—"}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: 11,
                    borderRadius: 999,
                    bgcolor:
                      detail?.liquidity === "High" ? "#dcfce7"
                      : detail?.liquidity === "Medium" ? "#fef9c3"
                      : detail?.liquidity === "Low" ? "#fee2e2"
                      : (theme) => theme.palette.border.light,
                    color:
                      detail?.liquidity === "High" ? "#16a34a"
                      : detail?.liquidity === "Medium" ? "#a16207"
                      : detail?.liquidity === "Low" ? "#dc2626"
                      : "text.muted",
                  }}
                />
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InvestorSecondaryMarket;
